import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { ftthPosteService, FtthPoste } from '../../services/ftthPosteService';
import { MapSelectorModal } from '../common';
import EscritorioSelect from '../selects/EscritorioSelect';
import { getPosteIconDataUrl, POSTE_ICON_OPTIONS, PosteIconId } from '../../utils/posteIcons';

const COLOR_PALETTE = [
  '#666666', '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF',
  '#FFD700', '#FFA500', '#FF6347', '#DC143C', '#006400', '#228B22', '#4169E1', '#4B0082',
  '#800080', '#808080', '#2F4F4F', '#696969',
];

export interface FtthPosteFormProps {
  onSuccess?: (poste: FtthPoste) => void;
  onCancel?: () => void;
  poste?: FtthPoste;
  coordinates?: { lat: number; lng: number };
  /** Dados de um poste para preencher o formulário em modo cópia (novo poste com dados baseados neste) */
  copyFrom?: FtthPoste;
  isFullscreen?: boolean;
}

interface FtthPosteFormData {
  identificacao: string;
  escritorio_id: number;
  latitude: string;
  longitude: string;
  informacoes: string;
  color: string;
  icon: PosteIconId;
}

const getInitialFormData = (
  poste?: FtthPoste | null,
  coordinates?: { lat: number; lng: number } | null,
  copyFrom?: FtthPoste | null
): FtthPosteFormData => {
  const source = poste ?? copyFrom;
  const lat = coordinates?.lat ?? source?.latitude ?? null;
  const lng = coordinates?.lng ?? source?.longitude ?? null;
  const identificacao = poste
    ? (poste.identificacao ?? '')
    : copyFrom
      ? `Cópia de ${copyFrom.identificacao ?? ''}`
      : '';
  return {
    identificacao,
    escritorio_id: source?.escritorio_id ?? 0,
    latitude: lat != null ? lat.toString() : '',
    longitude: lng != null ? lng.toString() : '',
    informacoes: source?.informacoes ?? '',
    color: source?.color ?? '#666666',
    icon: (source?.icon === 'com_trafo' || source?.icon === 'sem_trafo' ? source.icon : 'sem_trafo') as PosteIconId,
  };
};

const FtthPosteForm: React.FC<FtthPosteFormProps> = ({
  onSuccess,
  onCancel,
  poste,
  coordinates,
  copyFrom,
  isFullscreen,
}) => {
  const [formData, setFormData] = useState<FtthPosteFormData>(() =>
    getInitialFormData(poste, coordinates, copyFrom)
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const source = poste ?? copyFrom;
    if (source || coordinates) {
      setFormData((prev) => ({
        ...prev,
        ...(source && {
          identificacao: poste
            ? (poste.identificacao ?? '')
            : copyFrom
              ? `Cópia de ${copyFrom.identificacao ?? ''}`
              : prev.identificacao,
          escritorio_id: source.escritorio_id ?? 0,
          latitude: coordinates?.lat != null ? coordinates.lat.toString() : (source.latitude != null ? source.latitude.toString() : prev.latitude),
          longitude: coordinates?.lng != null ? coordinates.lng.toString() : (source.longitude != null ? source.longitude.toString() : prev.longitude),
          informacoes: source.informacoes ?? '',
          color: source.color ?? '#666666',
          icon: (source.icon === 'com_trafo' || source.icon === 'sem_trafo' ? source.icon : 'sem_trafo') as PosteIconId,
        }),
        ...(coordinates && !source && {
          latitude: coordinates.lat.toString(),
          longitude: coordinates.lng.toString(),
        }),
      }));
    }
  }, [poste, coordinates, copyFrom]);

  const handleChange = (field: keyof FtthPosteFormData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEscritorioChange = (value: number | number[] | null) => {
    const escritorioId = Array.isArray(value) ? (value[0] ?? null) : value;
    setFormData((prev) => ({ ...prev, escritorio_id: escritorioId ?? 0 }));
  };

  const handleOpenMapModal = () => {
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedLocation({ lat, lng });
      } else {
        setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
      }
    } else {
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
    }));
    setMapModalOpen(false);
  };

  const validateForm = (): boolean => {
    if (!formData.identificacao.trim()) {
      setError('Identificação é obrigatória');
      return false;
    }
    if (!formData.escritorio_id) {
      setError('Escritório é obrigatório');
      return false;
    }
    if ((formData.latitude && !formData.longitude) || (!formData.latitude && formData.longitude)) {
      setError('Informe ambas as coordenadas (latitude e longitude)');
      return false;
    }
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setError('Latitude deve estar entre -90 e 90');
        return false;
      }
      if (isNaN(lng) || lng < -180 || lng > 180) {
        setError('Longitude deve estar entre -180 e 180');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload = {
        identificacao: formData.identificacao.trim(),
        escritorio_id: formData.escritorio_id,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        informacoes: formData.informacoes.trim() || null,
        color: formData.color.trim() || '#666666',
        icon: formData.icon,
      };

      const result = poste
        ? await ftthPosteService.update(poste.id, payload)
        : await ftthPosteService.create(payload);

      if (result && onSuccess) {
        onSuccess(result);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar poste');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {poste ? 'Editar Poste' : copyFrom ? 'Copiar Poste' : 'Novo Poste'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Identificação"
            value={formData.identificacao}
            onChange={handleChange('identificacao')}
            required
            size="small"
            placeholder="Ex: POSTE-001"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EscritorioSelect
            value={formData.escritorio_id || null}
            onChange={handleEscritorioChange}
            size="small"
            fullWidth
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
            Localização
          </Typography>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Latitude"
            value={formData.latitude}
            onChange={handleChange('latitude')}
            size="small"
            type="text"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    startIcon={<LocationIcon />}
                    onClick={handleOpenMapModal}
                    sx={{ minWidth: 0, px: 1 }}
                  >
                    Mapa
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Longitude"
            value={formData.longitude}
            onChange={handleChange('longitude')}
            size="small"
            type="text"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Informações"
            value={formData.informacoes}
            onChange={handleChange('informacoes')}
            size="small"
            multiline
            rows={3}
            placeholder="Observações sobre o poste"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Ícone do poste
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {POSTE_ICON_OPTIONS.map((opt) => (
              <Box
                key={opt.id}
                onClick={() => setFormData((prev) => ({ ...prev, icon: opt.id }))}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1,
                  border: 2,
                  borderColor: formData.icon === opt.id ? 'primary.main' : 'divider',
                  bgcolor: formData.icon === opt.id ? 'action.selected' : 'transparent',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box
                  component="img"
                  src={getPosteIconDataUrl(opt.id, formData.color)}
                  alt={opt.label}
                  sx={{ width: 48, height: 48, objectFit: 'contain' }}
                />
                <Typography variant="caption" sx={{ mt: 0.5 }}>
                  {opt.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Cor no mapa
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {COLOR_PALETTE.map((c) => (
              <Box
                key={c}
                onClick={() => setFormData((prev) => ({ ...prev, color: c }))}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: c,
                  border: formData.color === c ? 3 : 1,
                  borderColor: formData.color === c ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.9 },
                }}
              />
            ))}
            <TextField
              size="small"
              value={formData.color}
              onChange={handleChange('color')}
              type="color"
              sx={{ width: 40, height: 28, p: 0, minWidth: 40, '& input': { cursor: 'pointer', height: 28, p: 0 } }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : poste ? 'Salvar' : 'Cadastrar'}
          </Button>
        </Grid>
      </Grid>

      {selectedLocation && (
        <MapSelectorModal
          open={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
          initialCoordinates={selectedLocation}
          onConfirm={handleConfirmMapLocation}
        />
      )}
    </Box>
  );
};

export default FtthPosteForm;
